import { ADDRESS_SHIPPING_CATEGORY } from 'services/constants/cart';
import { CART_ADDRESS_DESTINATION_TYPES as DESTINATION_TYPES } from 'stores';
import type { CountryIso, LocaleIso } from 'utils';
import type { DestinationType } from './types';

export { DESTINATION_TYPES };

export const ADDRESS_VALIDATION_CHOICES = {
  SUGGESTED: 'suggested',
  ORIGINAL: 'original'
} as const;

export type AddressValidationChoice = (typeof ADDRESS_VALIDATION_CHOICES)[keyof typeof ADDRESS_VALIDATION_CHOICES];
export const ADDRESS_VALIDATION_HEADING_ID = 'address-validation-heading';

export const ADDRESS_COUNTRY_CODE_ALIASES = {
  UK: 'GB'
} as const satisfies Partial<Record<string, CountryIso>>;

export const BILLING_FIELD_NAME_PREFIX = 'billing_';

export const ADDRESS_FORM_FIELD_NAMES = {
  DESTINATION_TYPE: 'destinationType',
  BILLING_DESTINATION_TYPE: 'billingDestinationType'
} as const;

export function isDestinationType(value: string): value is DestinationType {
  return Object.values(DESTINATION_TYPES).includes(value as DestinationType);
}

/** Destination type corresponding to each saved shipping address category. */
export const DESTINATION_TYPE_BY_SHIPPING_CATEGORY = {
  [ADDRESS_SHIPPING_CATEGORY.PRIVATE]: DESTINATION_TYPES.PRIVATE,
  [ADDRESS_SHIPPING_CATEGORY.COMPANY]: DESTINATION_TYPES.COMPANY,
  [ADDRESS_SHIPPING_CATEGORY.PICKUP_POINT]: DESTINATION_TYPES.PICKUP_POINT,
  [ADDRESS_SHIPPING_CATEGORY.PERSONAL]: DESTINATION_TYPES.PRIVATE
} as const;

/**
 * Locales whose saved address must belong to the exact locale country.
 * Other locales only reject addresses from non-European countries.
 */
export const LOCALES_REQUIRING_COUNTRY_MATCH: readonly LocaleIso[] = [
  'de-CH',
  'fr-CH',
  'it-CH',
  'da-DK',
  'en-GB',
  'sv-SE'
];

/** Locales where providing a phone number is mandatory in the address form. */
export const LOCALES_REQUIRING_PHONE_NUMBER: readonly LocaleIso[] = ['fr-FR', 'es-ES', 'da-DK'];

/** Locales that use CEWE address validation. */
export const LOCALES_USING_CEWE_ADDRESS_VALIDATION: readonly LocaleIso[] = [
  'de-DE',
  'de-AT',
  'de-CH',
  'fr-FR',
  'nl-NL',
  'fr-BE'
];

/** Country codes whose address form allows selecting countries outside the standard European list. */
export const COUNTRIES_WITH_GLOBAL_ADDRESS_SELECTION: readonly CountryIso[] = ['IE', 'CH', 'DK', 'SE', 'GB'];

/** Country codes that are not supported by the cart and must not be selectable. */
export const COUNTRIES_WITHOUT_CART: readonly CountryIso[] = ['BG', 'PL', 'HU', 'CZ'];
